import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ChevronRight, ArrowLeft } from 'lucide-react';
import { PDC_SPAS_PRODUCTS, PDC_SPAS_CATEGORIES } from '../data/pdcSpas';
import { PDCProductDetail, PDCProductCard } from '../components/pdcSpaParts';

const PDCProductPage = () => {
  const { productId } = useParams();
  const product = PDC_SPAS_PRODUCTS.find((p) => p.id === productId);

  if (!product) {
    return (
      <div className="pt-36 pb-24 min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
        <h1 className="font-['Barlow_Condensed'] text-4xl font-bold uppercase text-[#0E3D95] mb-3">Product Not Found</h1>
        <p className="text-slate-600 mb-6">Sorry, we couldn&apos;t find that PDC Spas model.</p>
        <Link to="/pdc-spas" className="btn-primary">Back to PDC Spas</Link>
      </div>
    );
  }

  const categoryName = PDC_SPAS_CATEGORIES.find((c) => c.id === product.category)?.name || 'PDC Spas';
  const related = PDC_SPAS_PRODUCTS.filter((p) => p.category === product.category && p.id !== product.id).slice(0, 4);

  return (
    <>
      <Helmet>
        <title>{`${product.name} | PDC Spas ${categoryName} | Leisure Wellness Naples`}</title>
        <meta name="description" content={`${product.name} by PDC Spas — ${product.description} Authorized Naples, FL dealer. Call for pricing.`} />
        <link rel="canonical" href={`https://www.leisurewellnessnaples.com/pdc-spas/${product.id}`} />
      </Helmet>

      <div className="pt-28 md:pt-32 pb-20" style={{
        background: 'linear-gradient(180deg, #ffffff 0%, #EAF2FF 30%, #EAF2FF 70%, #ffffff 100%)'
      }} data-testid="pdc-product-page">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center flex-wrap gap-1 text-sm text-slate-500 mb-6" aria-label="Breadcrumb">
            <Link to="/pdc-spas" className="hover:text-[#F88200]">PDC Spas</Link>
            <ChevronRight size={14} />
            <span className="text-slate-400">{categoryName}</span>
            <ChevronRight size={14} />
            <span className="text-[#0E3D95] font-semibold">{product.name}</span>
          </nav>

          <Link to="/pdc-spas" className="inline-flex items-center gap-1 text-sm font-semibold text-[#0E3D95] hover:text-[#F88200] mb-6" data-testid="pdc-back-link">
            <ArrowLeft size={16} /> Back to all PDC Spas
          </Link>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <PDCProductDetail product={product} />
          </motion.div>

          {/* Related models */}
          {related.length > 0 && (
            <div className="mt-16">
              <h2 className="font-['Barlow_Condensed'] text-3xl font-bold uppercase text-[#0E3D95] mb-6 pb-2 border-b-2 border-[#F88200]">
                More {categoryName}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {related.map((p) => (
                  <PDCProductCard key={p.id} product={p} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PDCProductPage;
